import React, { useState } from 'react';
import { X, UserPlus } from 'lucide-react';
import { createEmployee } from '../services/employeeApi';
import type { Employee } from '../services/employeeApi';
import { useI18n } from '../contexts/I18nContext';

interface QuickCreateEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (employee: Employee) => void;
  initialName?: string; // Nome já preenchido se o usuário estava digitando
  initialEmail?: string; // Email já preenchido se o usuário estava digitando
}

const QuickCreateEmployeeModal: React.FC<QuickCreateEmployeeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialName = '',
  initialEmail = ''
}) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: initialName,
    email: initialEmail,
    phone: '',
    document: '',
    zone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Resetar formulário quando modal abrir/fechar
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        name: initialName,
        email: initialEmail,
        phone: '',
        document: '',
        zone: ''
      });
      setError('');
    }
  }, [isOpen, initialName, initialEmail]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const employee = await createEmployee({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        document: formData.document.trim() || undefined,
        zone: formData.zone.trim() || undefined
      });

      onSuccess(employee);

      // Limpar formulário
      setFormData({ name: '', email: '', phone: '', document: '', zone: '' });
      onClose();
    } catch (err: any) {
      console.error('Erro ao criar funcionário:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao criar funcionário');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        width: '450px',
        maxWidth: '90vw'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserPlus size={20} color="#9333ea" />
            <h3 style={{ margin: 0, color: '#333' }}>{t('quickCreate.employee.title')}</h3>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={20} color="#666" />
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Nome * <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('common.requiredMark')}</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Email * <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('common.requiredMark')}</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="funcionario@exemplo.com"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Telefone <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('common.optional')}</span>
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(00) 00000-0000"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Documento (CPF/CNPJ) <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('common.optional')}</span>
            </label>
            <input
              type="text"
              value={formData.document}
              onChange={(e) => setFormData({ ...formData, document: e.target.value })}
              placeholder="000.000.000-00"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Zona <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('common.optional')}</span>
            </label>
            <input
              type="text"
              value={formData.zone}
              onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
              placeholder="Ex: Zona Norte"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              type="submit"
              disabled={isLoading || !formData.name.trim() || !formData.email.trim()}
              style={{
                flex: 1,
                backgroundColor: '#9333ea',
                color: 'white',
                border: 'none',
                padding: '10px',
                borderRadius: '4px',
                cursor: isLoading || !formData.name.trim() || !formData.email.trim() ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading || !formData.name.trim() || !formData.email.trim() ? 0.6 : 1
              }}
            >
              {isLoading ? 'Cadastrando...' : 'Cadastrar Funcionário'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                flex: 1,
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >{t('actions.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickCreateEmployeeModal;

